import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, createContext, useContext, useMemo } from "react";
import axios from "axios";
import ErrorBoundary from "@/components/ErrorBoundary";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

// Pages
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Agenda from "@/pages/Agenda";
import Servizi from "@/pages/Servizi";
import Magazzino from "@/pages/Magazzino";
import Clienti from "@/pages/Clienti";
import Appuntamenti from "@/pages/Appuntamenti";
import Preventivi from "@/pages/Preventivi";
import Cassa from "@/pages/Cassa";
import Impostazioni from "@/pages/Impostazioni";
import Analisi from "@/pages/Analisi";
import ColorBarPage from "@/pages/ColorBarPage";
import Consulenze from "@/pages/Consulenze";
import Fornitori from "@/pages/Fornitori";
import ConsulenzeAvanzate from "@/pages/ConsulenzeAvanzate";
import MarketingAutomazione from "@/pages/MarketingAutomazione";
import Layout from "@/components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with every request
axios.defaults.withCredentials = true;

// Axios interceptor per gestire errori di autenticazione e rete
let isRedirecting = false;

axios.interceptors.request.use(
  (config) => {
    // Aggiungi token a tutte le richieste se presente
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';
    
    // Non fare redirect per chiamate di autenticazione iniziali
    const isAuthCheck = requestUrl.includes('/auth/me') || requestUrl.includes('/auth/login');
    const isOnLoginPage = window.location.pathname === '/login' || window.location.pathname === '/reset-password';
    
    // Se errore 401 (non autorizzato), non stiamo già reindirizzando, 
    // non è una chiamata auth e non siamo sulla pagina di login
    if (error.response?.status === 401 && !isRedirecting && !isAuthCheck && !isOnLoginPage) {
      isRedirecting = true;
      // Pulisci storage e reindirizza al login
      sessionStorage.removeItem('token');
      localStorage.removeItem('token');
      // Usa un piccolo delay per evitare race conditions
      setTimeout(() => {
        window.location.href = '/login';
        isRedirecting = false;
      }, 100);
      return Promise.reject(error);
    }
    
    // Per errori di rete (es. offline, timeout), ritenta una volta
    if (!error.response && !originalRequest._retry) {
      originalRequest._retry = true;
      // Aspetta 1 secondo e ritenta
      await new Promise(resolve => setTimeout(resolve, 1000));
      return axios(originalRequest);
    }
    
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Usa sessionStorage invece di localStorage per maggiore sicurezza
  // sessionStorage viene cancellato quando si chiude il browser
  const [token, setToken] = useState(sessionStorage.getItem('token') || localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      // Try to get user info - will use httpOnly cookie automatically
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Keep any existing token for backwards compatibility with Authorization headers
      const storedToken = sessionStorage.getItem('token') || localStorage.getItem('token');
      if (storedToken) {
        setToken(storedToken);
      }
      return true;
    } catch (error) {
      // If cookie auth fails, try with sessionStorage/localStorage token (backwards compatibility)
      const storedToken = sessionStorage.getItem('token') || localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
          return true;
        } catch {
          // Pulisci entrambi gli storage
          sessionStorage.removeItem('token');
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          return false;
        }
      } else {
        setUser(null);
        return false;
      }
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      await checkAuth();
      setLoading(false);
    };
    initAuth();
  }, []);

  // Ricontrolla l'autenticazione quando l'app torna in primo piano
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && user) {
        // L'app è tornata in primo piano, verifica che la sessione sia ancora valida
        const isValid = await checkAuth();
        if (!isValid) {
          // Sessione scaduta, l'interceptor gestirà il redirect
          window.location.href = '/login';
        }
      }
    };

    const handleOnline = async () => {
      // La connessione è tornata, ricontrolla l'autenticazione
      if (user) {
        await checkAuth();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [user]);

  const login = async (email, password, rememberMe = true) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    // Se "Resta connesso" attivo → localStorage (persistente). Altrimenti → sessionStorage (scade alla chiusura browser).
    if (rememberMe) {
      localStorage.setItem('token', newToken);
      sessionStorage.removeItem('token');
    } else {
      sessionStorage.setItem('token', newToken);
      localStorage.removeItem('token');
    }
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, nome, rememberMe = true) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, nome });
    const { token: newToken, user: userData } = response.data;
    if (rememberMe) {
      localStorage.setItem('token', newToken);
      sessionStorage.removeItem('token');
    } else {
      sessionStorage.setItem('token', newToken);
      localStorage.removeItem('token');
    }
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // Ignore errors on logout
    }
    // Pulisci entrambi gli storage per sicurezza
    sessionStorage.removeItem('token');
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user, 
    token, 
    login, 
    register, 
    logout, 
    loading
  }), [user, token, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="agenda" element={<ErrorBoundary><Agenda /></ErrorBoundary>} />
              <Route path="colorbar" element={<ColorBarPage />} />
              <Route path="servizi" element={<Servizi />} />
              <Route path="magazzino" element={<Magazzino />} />
              <Route path="clienti" element={<Clienti />} />
              <Route path="appuntamenti" element={<Appuntamenti />} />
              <Route path="preventivi" element={<Preventivi />} />
              <Route path="cassa" element={<Cassa />} />
              <Route path="analisi" element={<Analisi />} />
              <Route path="consulenze" element={<Consulenze />} />
              <Route path="consulenze-avanzate" element={<ConsulenzeAvanzate />} />
              <Route path="fornitori" element={<Fornitori />} />
              <Route path="marketing-automazione" element={<MarketingAutomazione />} />
              <Route path="impostazioni" element={<Impostazioni />} />
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
          <PWAInstallPrompt />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
